import {
  type ConnectorDto,
  CreateSourceRequestSchema,
  type CreateSourceResponse,
  MIN_POLL_INTERVAL_S,
  type SourceDto,
  UpdateSourceRequestSchema,
} from '@vision360/contracts';
import { Check, Copy } from 'lucide-react';
import { type FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '@/lib/api/errors';
import { apiUrl } from '@/lib/api/origin';
import { NumberField, SelectField, TextField } from '@/widgets/_sdk/fields';
import { useConnectors, useCreateSource, useUpdateSource } from './api';
import { hasInvalidJson, initialValue, isRecord, type JsonSchema, SchemaForm } from './schema-form';

/**
 * Create or edit a source from the web (step 36a, PRD VS-025; until now only
 * the `source-create` CLI could). The config form is generated from the
 * connector's JSON Schema. Secrets are write-only: blank on edit means
 * "unchanged" and nothing is sent. A webhook source's secret is shown once,
 * right after creation, like the CLI does.
 */

const FALLBACK_POLL_S = 60;

interface Draft {
  connectorId: string;
  name: string;
  pollIntervalS: number | undefined;
  config: Record<string, unknown>;
  secrets: Record<string, string>;
}

const initialConfig = (connector: ConnectorDto | undefined): Record<string, unknown> => {
  const value = connector ? initialValue(connector.configSchema) : undefined;
  return isRecord(value) ? value : {};
};

const typedSecrets = (secrets: Record<string, string>) =>
  Object.fromEntries(Object.entries(secrets).filter(([, v]) => v !== ''));

/** Client-side issues, first per top-level field. */
function clientErrors(error: z.ZodError | undefined): Record<string, true> {
  const out: Record<string, true> = {};
  for (const issue of error?.issues ?? []) out[String(issue.path[0] ?? 'other')] = true;
  return out;
}

interface ServerErrors {
  fields: Record<string, string>;
  config: Record<string, string>;
  other: string[];
}

/**
 * A 400 VALIDATION lists `{ path, message }`. Config paths come relative to
 * the config (`stops.0.id`) from the connector check, or prefixed (`config.x`)
 * from the request check; each lands on its top-level property when the form
 * shows one, else at the top.
 */
export function serverErrors(error: unknown, configKeys: readonly string[]): ServerErrors {
  const out: ServerErrors = { fields: {}, config: {}, other: [] };
  if (
    !(error instanceof ApiError) ||
    error.code !== 'VALIDATION' ||
    !Array.isArray(error.details)
  ) {
    return out;
  }
  for (const issue of error.details) {
    if (!isRecord(issue) || typeof issue['path'] !== 'string') continue;
    const message = typeof issue['message'] === 'string' ? issue['message'] : '';
    const path = issue['path'].split('.');
    const inConfig = path[0] === 'config' ? path.slice(1) : path;
    const head = inConfig[0] ?? '';
    if (configKeys.includes(head)) out.config[head] ??= message;
    else if (path[0] === 'name' || path[0] === 'pollIntervalS') out.fields[path[0]] ??= message;
    else out.other.push(message);
  }
  return out;
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation('sources');
  const id = useId();
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setState('copied');
    } catch {
      setState('failed');
    }
  };
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2">
        <Input
          id={id}
          readOnly
          value={value}
          className="font-mono text-xs"
          onFocus={(e) => e.target.select()}
        />
        <Button
          type="button"
          variant="outline"
          className="shrink-0"
          aria-label={t('dialog.webhook.copyLabel', { what: label })}
          onClick={() => void copy()}
        >
          {state === 'copied' ? (
            <Check aria-hidden="true" className="h-4 w-4" />
          ) : (
            <Copy aria-hidden="true" className="h-4 w-4" />
          )}
          {t('dialog.webhook.copy')}
        </Button>
      </div>
      <output aria-live="polite" className="block text-xs text-muted-foreground">
        {state === 'copied' && t('dialog.webhook.copied')}
        {state === 'failed' && t('dialog.webhook.copyFailed')}
      </output>
    </div>
  );
}

function WebhookCreated({
  created,
  onDone,
}: {
  created: CreateSourceResponse;
  onDone: () => void;
}) {
  const { t } = useTranslation('sources');
  const path = created.source.webhookPath ?? '';
  return (
    <div className="animate-enter space-y-4">
      <DialogTitle>{t('dialog.webhook.title')}</DialogTitle>
      <DialogDescription>{t('dialog.webhook.intro')}</DialogDescription>
      <CopyRow
        label={t('dialog.webhook.url')}
        value={new URL(apiUrl(path), window.location.origin).href}
      />
      <CopyRow label={t('dialog.webhook.secret')} value={created.webhookSecret ?? ''} />
      <div className="flex justify-end">
        <Button type="button" className="h-11" onClick={onDone}>
          {t('dialog.webhook.done')}
        </Button>
      </div>
    </div>
  );
}

function SecretFields({
  keys,
  values,
  editing,
  onChange,
}: {
  keys: readonly string[];
  values: Record<string, string>;
  editing: boolean;
  onChange: (key: string, value: string) => void;
}) {
  const { t } = useTranslation('sources');
  const id = useId();
  if (keys.length === 0) return null;
  return (
    <fieldset className="space-y-3 rounded-lg border p-3" aria-describedby={`${id}-hint`}>
      <legend className="px-1 text-sm font-medium">{t('dialog.secrets')}</legend>
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        {editing ? t('dialog.secretsHintEdit') : t('dialog.secretsHintNew')}
      </p>
      {keys.map((key) => (
        <div key={key} className="space-y-1.5">
          <Label htmlFor={`${id}-${key}`}>{t(`secretNames.${key}`, { defaultValue: key })}</Label>
          <Input
            id={`${id}-${key}`}
            type="password"
            autoComplete="new-password"
            spellCheck={false}
            value={values[key] ?? ''}
            onChange={(e) => onChange(key, e.target.value)}
          />
        </div>
      ))}
    </fieldset>
  );
}

export function SourceDialog({
  source,
  onClose,
}: {
  source?: SourceDto | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation('sources');
  const connectors = useConnectors();
  const create = useCreateSource();
  const update = useUpdateSource();
  const saving = source ? update : create;
  const [draft, setDraft] = useState<Draft>(() => ({
    connectorId: source?.connectorId ?? '',
    name: source?.name ?? '',
    pollIntervalS: source?.pollIntervalS ?? undefined,
    config: source?.config ?? {},
    secrets: {},
  }));
  const [submitted, setSubmitted] = useState(false);
  const [created, setCreated] = useState<CreateSourceResponse | null>(null);
  const headingId = useId();

  const list = connectors.data?.connectors ?? [];
  const connector = list.find((c) => c.id === draft.connectorId);
  const mode = connector?.mode ?? source?.mode;
  const schema: JsonSchema = connector?.configSchema ?? {};
  const configKeys = isRecord(schema['properties']) ? Object.keys(schema['properties']) : [];

  const set = (patch: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    // A server verdict is about the values it saw; editing moves on from it.
    if (saving.isError) saving.reset();
  };

  const chooseConnector = (connectorId: string) => {
    const next = list.find((c) => c.id === connectorId);
    set({
      connectorId,
      config: initialConfig(next),
      secrets: {},
      pollIntervalS: next?.defaultPollIntervalS ?? undefined,
    });
  };

  const poll = mode === 'poll' && draft.pollIntervalS !== undefined;
  const secrets = typedSecrets(draft.secrets);
  const common = {
    name: draft.name.trim(),
    config: draft.config,
    ...(poll ? { pollIntervalS: draft.pollIntervalS } : {}),
  };
  const patch = source
    ? UpdateSourceRequestSchema.safeParse({
        ...common,
        ...(Object.keys(secrets).length > 0 ? { secrets } : {}),
      })
    : null;
  const body = source
    ? null
    : CreateSourceRequestSchema.safeParse({ ...common, connectorId: draft.connectorId, secrets });
  const parseError = (patch ?? body)?.error;
  const invalidJson = hasInvalidJson(draft.config);
  const local = submitted ? clientErrors(parseError) : {};
  const remote = serverErrors(saving.error, configKeys);
  const fieldError = (field: 'connectorId' | 'name' | 'pollIntervalS') =>
    local[field]
      ? t(`dialog.errors.${field}`)
      : remote.fields[field] !== undefined
        ? t('dialog.rejected', { message: remote.fields[field] })
        : undefined;
  const configErrors = Object.fromEntries(
    Object.entries(remote.config).map(([k, message]) => [k, t('dialog.rejected', { message })]),
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (invalidJson) return;
    if (source && patch?.success) {
      update.mutate({ id: source.id, patch: patch.data }, { onSuccess: onClose });
    } else if (body?.success) {
      create.mutate(body.data, {
        onSuccess: (response) => (response.webhookSecret ? setCreated(response) : onClose()),
      });
    }
  };

  let content: React.ReactNode;
  if (connectors.isPending) {
    content = (
      <output aria-live="polite" className="flex items-center gap-2 text-muted-foreground">
        <Spinner />
        {t('dialog.connectorsLoading')}
      </output>
    );
  } else if (connectors.isError) {
    content = (
      <Alert variant="destructive" role="alert">
        {t('dialog.connectorsError')}
      </Alert>
    );
  } else {
    content = (
      <form className="space-y-4" noValidate onSubmit={submit}>
        {!source && (
          <SelectField
            label={t('dialog.connector')}
            value={draft.connectorId}
            placeholder={t('dialog.connectorPlaceholder')}
            options={list
              .filter((c) => c.mode !== 'stream')
              .map((c) => ({
                value: c.id,
                label: t('dialog.connectorOption', {
                  name: c.name,
                  mode: t(`dialog.modes.${c.mode}`),
                }),
              }))}
            onChange={chooseConnector}
            error={fieldError('connectorId')}
          />
        )}
        {source && !connector && (
          <Alert role="status">{t('dialog.connectorUnknown', { id: source.connectorId })}</Alert>
        )}
        {(source || connector) && (
          <>
            <TextField
              label={t('dialog.name')}
              value={draft.name}
              onChange={(name) => set({ name })}
              error={fieldError('name')}
            />
            {mode === 'poll' && (
              <div className="space-y-1">
                <NumberField
                  label={t('dialog.pollInterval')}
                  value={draft.pollIntervalS}
                  min={MIN_POLL_INTERVAL_S}
                  max={86_400}
                  step={1}
                  onChange={(pollIntervalS) => set({ pollIntervalS })}
                  error={fieldError('pollIntervalS')}
                />
                <p className="text-xs text-muted-foreground">
                  {t('dialog.pollIntervalHint', {
                    min: MIN_POLL_INTERVAL_S,
                    fallback: connector?.defaultPollIntervalS ?? FALLBACK_POLL_S,
                  })}
                </p>
              </div>
            )}
            <section className="space-y-3" aria-labelledby={headingId}>
              <h3 id={headingId} className="text-sm font-semibold">
                {t('dialog.config')}
              </h3>
              <SchemaForm
                // A new connector is a new form: JSON textareas re-read their value.
                key={draft.connectorId}
                connectorId={draft.connectorId}
                schema={schema}
                value={draft.config}
                onChange={(config) => set({ config })}
                errors={configErrors}
              />
            </section>
            <SecretFields
              keys={connector?.secretKeys ?? []}
              values={draft.secrets}
              editing={source !== undefined}
              onChange={(key, value) => set({ secrets: { ...draft.secrets, [key]: value } })}
            />
          </>
        )}

        <div aria-live="polite" className="space-y-2">
          {submitted && (parseError !== undefined || invalidJson) && (
            <p className="text-sm text-destructive">{t('dialog.invalid')}</p>
          )}
          {saving.isError && (
            <Alert variant="destructive" role="alert">
              {remote.other.length > 0
                ? t('dialog.rejected', { message: remote.other.join(' · ') })
                : t('dialog.saveFailed')}
            </Alert>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" className="h-11" onClick={onClose}>
            {t('dialog.cancel')}
          </Button>
          <Button type="submit" className="h-11" disabled={saving.isPending}>
            {saving.isPending && <Spinner />}
            {saving.isPending ? t('dialog.saving') : source ? t('dialog.save') : t('dialog.create')}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        closeLabel={t('dialog.close')}
        className="animate-enter max-w-2xl"
        // An open picker list takes the first Esc for itself (LookupField closes it).
        onEscapeKeyDown={(e) => {
          if (
            e.target instanceof HTMLElement &&
            e.target.getAttribute('aria-expanded') === 'true'
          ) {
            e.preventDefault();
          }
        }}
      >
        {created ? (
          <WebhookCreated created={created} onDone={onClose} />
        ) : (
          <>
            <DialogTitle>
              {source ? t('dialog.titleEdit', { name: source.name }) : t('dialog.titleNew')}
            </DialogTitle>
            <DialogDescription>{t('dialog.description')}</DialogDescription>
            {content}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
